import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { WEBSITE_TOKEN, CHATWOOT_URL } from "../utils/apiConfig";
import "../App.css";

function SalesChatPage() {
  const { user, logout, isLoggingOut } = useAuth();
  const [error, setError] = useState(null);
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const scriptLoadedRef = useRef(false);
  const widgetLoadedRef = useRef(false);
  const timeoutRef = useRef(null);
  // Store all event handlers for proper cleanup
  const eventHandlersRef = useRef({
    ready: [],
    message: [],
    error: [],
    userReady: [],
  });

  // Setup global error suppression on mount for logout scenarios
  useEffect(() => {
    // Keep references to check if we need to suppress errors
    const checkErrorSuppression = () => {
      return window.isLoggingOut || window.isLoggingIn;
    };

    // Enhance global error handlers to check our flags
    const originalWindowError = window.onerror;
    const enhancedWindowError = function(...args) {
      if (checkErrorSuppression()) {
        return true; // Suppress
      }
      return originalWindowError ? originalWindowError.apply(this, args) : false;
    };

    const originalUnhandled = window.onunhandledrejection;
    const enhancedUnhandled = function(event) {
      if (checkErrorSuppression()) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return originalUnhandled ? originalUnhandled.apply(this, arguments) : false;
    };

    window.onerror = enhancedWindowError;
    window.onunhandledrejection = enhancedUnhandled;

    // Cleanup on unmount
    return () => {
      if (window.onerror === enhancedWindowError) {
        window.onerror = originalWindowError;
      }
      if (window.onunhandledrejection === enhancedUnhandled) {
        window.onunhandledrejection = originalUnhandled;
      }
    };
  }, []);

  const CHATWOOT_BASE_URL = CHATWOOT_URL || "http://localhost:3000";

  // Override Chatwoot styles to make it full screen but leave space for user bar
  const overrideChatwootStyles = () => {
    const checkAndOverride = () => {
      const chatwootStyles = document.getElementById("cw-widget-styles");
      if (chatwootStyles) {
        // Calculate height leaving space for user info bar (approximately 60px)
        const customStyles = `
          .woot-widget-holder {
            width: 100vw !important;
            height: calc(100vh - 60px) !important;
            max-width: 100vw !important;
            max-height: calc(100vh - 60px) !important;
            position: fixed !important;
            top: 60px !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            z-index: 9999 !important;
            border-radius: 0 !important;
          }
          /* Hide close button and prevent closing */
          .woot-widget-bubble,
          .woot--close-button,
          [data-testid="close-button"],
          .woot-widget-holder .woot-widget-bubble,
          button[aria-label*="close"],
          button[aria-label*="Close"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
          /* Ensure chat is always open */
          .woot-widget-holder {
            display: block !important;
          }
          .woot-widget-holder.woot-widget-holder--open {
            display: block !important;
          }
        `;

        chatwootStyles.textContent += customStyles;
        return true;
      }
      return false;
    };

    // Try to override immediately
    if (!checkAndOverride()) {
      // If not found, try again after a delay
      setTimeout(() => {
        if (!checkAndOverride()) {
          // Last attempt with longer delay
          setTimeout(checkAndOverride, 1000);
        }
      }, 500);
    }
  };

  // Load Chatwoot widget on mount
  useEffect(() => {
    loadChatwootWidget();

    return () => {
      cleanupChatwoot();
    };
  }, []);

  // Set user identity in Chatwoot when user is available and widget is ready
  useEffect(() => {
    if (!user) return;

    const chatwootIdentifierHash = localStorage.getItem("chatwoot_identifier_hash");
    if (!chatwootIdentifierHash) return;

    const setUserIdentity = () => {
      // Wait for Chatwoot to be fully ready
      if (window.$chatwoot && typeof window.$chatwoot.setUser === 'function') {
        try {
          const identifier = user.email || user.sub;
          window.$chatwoot.setUser(identifier, {
            email: user.email,
            name: user.name,
            identifier_hash: chatwootIdentifierHash,
          });
          console.log("Chatwoot user identity set successfully");
        } catch (err) {
          console.error("Error setting Chatwoot user identity:", err);
        }
      } else {
        // If Chatwoot is not ready yet, wait for the ready event
        const readyHandler = () => {
          if (window.$chatwoot && typeof window.$chatwoot.setUser === 'function') {
            try {
              const identifier = user.email || user.sub;
              window.$chatwoot.setUser(identifier, {
                email: user.email,
                name: user.name,
                identifier_hash: chatwootIdentifierHash,
              });
              console.log("Chatwoot user identity set successfully (after ready event)");
            } catch (err) {
              console.error("Error setting Chatwoot user identity:", err);
            }
            // Remove listener after use
            window.removeEventListener("chatwoot:ready", readyHandler);
            eventHandlersRef.current.userReady = eventHandlersRef.current.userReady.filter(h => h !== readyHandler);
          }
        };
        window.addEventListener("chatwoot:ready", readyHandler);
        eventHandlersRef.current.userReady.push(readyHandler);
        // Return cleanup function
        return () => {
          window.removeEventListener("chatwoot:ready", readyHandler);
          eventHandlersRef.current.userReady = eventHandlersRef.current.userReady.filter(h => h !== readyHandler);
        };
      }
    };

    // Try to set user identity immediately if widget is loaded
    if (isWidgetLoaded) {
      setUserIdentity();
    } else {
      // If widget is not loaded yet, wait for it
      const widgetReadyHandler = () => {
        setUserIdentity();
        window.removeEventListener("chatwoot:ready", widgetReadyHandler);
        eventHandlersRef.current.userReady = eventHandlersRef.current.userReady.filter(h => h !== widgetReadyHandler);
      };
      window.addEventListener("chatwoot:ready", widgetReadyHandler);
      eventHandlersRef.current.userReady.push(widgetReadyHandler);
      return () => {
        window.removeEventListener("chatwoot:ready", widgetReadyHandler);
        eventHandlersRef.current.userReady = eventHandlersRef.current.userReady.filter(h => h !== widgetReadyHandler);
      };
    }
  }, [user, isWidgetLoaded]);

  const cleanupChatwoot = () => {
    // Wrap EVERYTHING in try-catch to ensure NO errors escape
    try {
      // Remove all stored event listeners
      try {
        if (eventHandlersRef.current?.ready) {
          eventHandlersRef.current.ready.forEach(handler => {
            try {
              if (handler && typeof handler === 'function') {
                window.removeEventListener("chatwoot:ready", handler);
              }
            } catch (e) {}
          });
        }
      } catch (e) {}

      try {
        if (eventHandlersRef.current?.message) {
          eventHandlersRef.current.message.forEach(handler => {
            try {
              if (handler && typeof handler === 'function') {
                window.removeEventListener("chatwoot:on-message", handler);
              }
            } catch (e) {}
          });
        }
      } catch (e) {}

      try {
        if (eventHandlersRef.current?.error) {
          eventHandlersRef.current.error.forEach(handler => {
            try {
              if (handler && typeof handler === 'function') {
                window.removeEventListener("chatwoot:error", handler);
              }
            } catch (e) {}
          });
        }
      } catch (e) {}

      try {
        if (eventHandlersRef.current?.userReady) {
          eventHandlersRef.current.userReady.forEach(handler => {
            try {
              if (handler && typeof handler === 'function') {
                window.removeEventListener("chatwoot:ready", handler);
              }
            } catch (e) {}
          });
        }
      } catch (e) {}

      // Clear all stored handlers
      try {
        if (eventHandlersRef.current) {
          eventHandlersRef.current.ready = [];
          eventHandlersRef.current.message = [];
          eventHandlersRef.current.error = [];
          eventHandlersRef.current.userReady = [];
        }
      } catch (e) {}

      // Close Chatwoot widget if open
      try {
        if (window.$chatwoot && typeof window.$chatwoot.toggle === 'function') {
          window.$chatwoot.toggle("close");
        }
      } catch (e) {}

      // Remove Chatwoot script if exists
      try {
        const existingScript = document.querySelector('script[src*="/packs/js/sdk.js"]');
        if (existingScript) {
          existingScript.remove();
        }
      } catch (e) {}

      // Remove widget container and all Chatwoot-related DOM elements
      try {
        const widgetContainer = document.getElementById("chatwoot-widget-container");
        if (widgetContainer) {
          widgetContainer.remove();
        }
      } catch (e) {}

      // Remove any Chatwoot widget holders
      try {
        const widgetHolders = document.querySelectorAll('.woot-widget-holder, .woot-widget-bubble');
        widgetHolders.forEach(el => {
          try {
            el.remove();
          } catch (e) {}
        });
      } catch (e) {}

      // Clear global variables safely
      try {
        if (window.$chatwoot && typeof window.$chatwoot.reset === 'function') {
          window.$chatwoot.reset();
        }
      } catch (e) {}

      // Delete global variables
      try {
        delete window.$chatwoot;
      } catch (e) {}
      try {
        delete window.chatwootSDK;
      } catch (e) {}
      try {
        delete window.chatwootSettings;
      } catch (e) {}

      // Clear timeout
      try {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } catch (e) {}

      // Update state
      try {
        setIsWidgetLoaded(false);
        scriptLoadedRef.current = false;
        widgetLoadedRef.current = false;
      } catch (e) {}
    } catch (error) {
      // Absolutely silent - no errors will escape
    }
  };

  const loadChatwootWidget = () => {
    const websiteToken = WEBSITE_TOKEN;

    if (!websiteToken || websiteToken === "REACT_APP_WEBSITE_TOKEN_PLACEHOLDER") {
      setError("Token do Chatwoot não disponível. Configure o REACT_APP_WEBSITE_TOKEN no .env");
      return;
    }

    if (scriptLoadedRef.current) {
      cleanupChatwoot();
    }

    widgetLoadedRef.current = false;
    setIsWidgetLoaded(false);
    setError(null);

    // Chatwoot Widget Configuration
    window.chatwootSettings = {
      hideMessageBubble: true, // Hide bubble - chat will always be open
      position: "right",
      locale: "pt",
      type: "expanded_bubble", // Start expanded
      darkMode: "light",
      showPopoutButton: false,
      showUnreadMessagesDialog: false,
      useBrowserLanguage: false,
      enableFileUpload: true,
      enableEmojiPicker: true,
      welcomeTitle: "Joninhas AI",
      welcomeDescription: "Assistente de vendas Lugotech.",
      availableMessage: "Estamos disponíveis para ajudar.",
      unavailableMessage: "Estamos ausentes",
    };

    // Create and load Chatwoot script
    const script = document.createElement("script");
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.defer = true;
    script.async = true;

    // Timeout to detect invalid token
    timeoutRef.current = setTimeout(() => {
      if (!widgetLoadedRef.current) {
        setError(
          "Erro ao carregar o widget do Chatwoot. Verifique se o token está correto."
        );
        setIsWidgetLoaded(false);
        cleanupChatwoot();
      }
    }, 5000);

    script.onload = () => {
      if (window.chatwootSDK && typeof window.chatwootSDK.run === "function") {
        try {
          // Validate token before initializing
          if (!websiteToken || !websiteToken.trim()) {
            throw new Error("Token inválido: token vazio");
          }

          // Validate base URL
          if (!CHATWOOT_BASE_URL || CHATWOOT_BASE_URL.trim() === "") {
            throw new Error("URL base do Chatwoot não configurada");
          }

          // Initialize Chatwoot SDK
          window.chatwootSDK.run({
            websiteToken: websiteToken.trim(),
            baseUrl: CHATWOOT_BASE_URL.trim(),
          });

          // Setup ready handler
          const readyHandler = () => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            widgetLoadedRef.current = true;
            setIsWidgetLoaded(true);
            setError(null);

            // Override styles to make it full screen
            overrideChatwootStyles();

            // Open chat automatically when ready (full screen) and prevent closing
            setTimeout(() => {
              if (window.$chatwoot) {
                // Open chat automatically
                window.$chatwoot.toggle("open");

                // Hide close button and prevent closing
                const hideCloseButton = () => {
                  const widgetHolder = document.querySelector('.woot-widget-holder');
                  if (widgetHolder) {
                    // Hide close button
                    const closeButton = widgetHolder.querySelector('.woot-widget-bubble, .woot--close-button, [data-testid="close-button"]');
                    if (closeButton) {
                      closeButton.style.display = 'none';
                    }

                    // Override toggle to prevent closing
                    const originalToggle = window.$chatwoot.toggle;
                    window.$chatwoot.toggle = function(state) {
                      // Only allow opening, not closing
                      if (state === "open" || state === undefined) {
                        return originalToggle.call(this, "open");
                      }
                      // If trying to close, just keep it open
                      return originalToggle.call(this, "open");
                    };
                  }
                };

                // Try to hide close button immediately and on intervals
                hideCloseButton();
                setInterval(hideCloseButton, 1000);

                // Monitor for close attempts and reopen
                const checkAndReopen = () => {
                  const widgetHolder = document.querySelector('.woot-widget-holder');
                  if (widgetHolder && !widgetHolder.classList.contains('woot-widget-holder--open')) {
                    window.$chatwoot.toggle("open");
                  }
                };
                setInterval(checkAndReopen, 500);
              }
            }, 500);
          };

          window.addEventListener("chatwoot:ready", readyHandler);
          eventHandlersRef.current.ready.push(readyHandler);

          // Keep chat open when receiving messages
          const messageHandler = () => {
            if (window.$chatwoot) {
              setTimeout(() => {
                const widgetHolder = document.querySelector('.woot-widget-holder');
                if (widgetHolder && !widgetHolder.classList.contains('woot-widget-holder--open')) {
                  window.$chatwoot.toggle("open");
                }
              }, 100);
            }
          };

          window.addEventListener("chatwoot:on-message", messageHandler);
          eventHandlersRef.current.message.push(messageHandler);

          // Setup error handler
          const errorHandler = (event) => {
            console.error("Chatwoot error:", event);
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            setError("Erro ao inicializar o widget do Chatwoot");
            setIsWidgetLoaded(false);
            cleanupChatwoot();
          };

          window.addEventListener("chatwoot:error", errorHandler);
          eventHandlersRef.current.error.push(errorHandler);
        } catch (err) {
          console.error("Error initializing Chatwoot:", err);
          setError("Erro ao inicializar o widget do Chatwoot");
          setIsWidgetLoaded(false);
          cleanupChatwoot();
        }
      } else {
        setError("SDK do Chatwoot não disponível");
        setIsWidgetLoaded(false);
      }
    };

    script.onerror = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setError(
        `Erro ao carregar o script do Chatwoot. Verifique se a URL está correta: ${CHATWOOT_BASE_URL}`
      );
      setIsWidgetLoaded(false);
    };

    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  };

  const handleLogout = () => {
    // IMMEDIATELY mark that we're logging out and suppress ALL errors
    window.isLoggingOut = true;

    // FIRST: Suppress ALL errors globally BEFORE doing anything else
    window.onerror = () => true;
    window.onunhandledrejection = (e) => { e.preventDefault(); return true; };
    console.error = () => {};
    console.warn = () => {};

    // Disable React Error Overlay
    if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.iframeHandler = () => {};
    }

    // Now do the logout process inside a setTimeout to ensure error handlers are set
    setTimeout(() => {
      try {
        // Cleanup Chatwoot widget before logout to prevent errors
        cleanupChatwoot();
      } catch (e) {
        // Ignore
      }

      // Wait a bit for cleanup then logout
      setTimeout(() => {
        try {
          logout();
        } catch (e) {
          // Ignore and redirect anyway
          window.location.href = '/';
        }
      }, 100);
    }, 0);
  };

  return (
    <div className="app-container">
      {/* Loading overlay during logout */}
      {isLoggingOut && (
        <div className="logout-loading-overlay">
          <div className="logout-loading-content">
            <div className="loading-spinner"></div>
            <h2>A processar logout...</h2>
            <p>Por favor, aguarde enquanto completamos o logout.</p>
          </div>
        </div>
      )}

      {/* User Info Bar */}
      {user && (
        <div className="user-info-bar">
          <span className="user-info-name">{user.name || user.email}</span>
          <button
            onClick={handleLogout}
            className="user-info-logout-button"
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "A processar..." : "Logout"}
          </button>
        </div>
      )}

      {/* Main Content - Empty, only chat widget visible */}
      <main className="main-container">
        {/* Error Display - only show if there's an error */}
        {error && (
          <div className="error-container-page">
            <div className="error-message">
              <strong>⚠️ Atenção:</strong> {error}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default SalesChatPage;
